import React, {
  createContext,
  useState,
  useEffect,
  useRef,
  useContext,
  useMemo,
} from 'react'

import { TableProps } from 'antd'
import { FilterValue, SorterResult } from 'antd/lib/table/interface'
import { get, set, remove } from 'local-storage'
import { cloneDeep, isNull } from 'lodash'
import { useRouter } from 'next/router'

import { buildDateFilter } from '@/components/shared-components/MultiDatePicker'

import { SortOrder } from 'types/api.graphql'

type DateFilterValue = ReturnType<typeof buildDateFilter>

type BaseFiltersType = Record<
  string,
  Array<object> | FilterValue | DateFilterValue | null
>

/**
 * @param extra - is used for persisting config across sessions that is not within a table. i.e remember the last selected tab or view
 * @param session - is used for short lived config that is not persisted across sessions. i.e. the currently selected date for a given page
 */
export type FilterObjectType<T extends object = Record<string, unknown>> = {
  pagination?: TableProps<unknown>['pagination']
  filters?: T & BaseFiltersType
  sort?: SorterResult<unknown> & {
    sortKey?: string
    direction?: SortOrder
  }
  extra?: Record<string, unknown>
  session?: Record<string, unknown>
}

type FilterContextType<T extends object = Record<string, unknown>> = {
  updateFilters: (filterObject: FilterObjectType) => void
  clearFilters: () => void
  clearAllFilters: () => void
  filterStore: {
    [pathname: string]: FilterObjectType
  }
  routeFilters: FilterObjectType<T>
}

export const FilterContext = createContext<FilterContextType>({
  routeFilters: {},
  filterStore: {},
  clearFilters: () => {},
  clearAllFilters: () => {},
  updateFilters: () => {},
})

const LS_FILTER_KEY = 'buildpass-filters'
const DEFAULT_FILTERS = {}

// Removes session filters from the filter object before persisting to local storage
const stripSession = (filterStore: FilterContextType['filterStore']) => {
  const strippedStore = cloneDeep(filterStore)

  Object.keys(strippedStore).forEach((key) => {
    delete strippedStore[key].session
  })

  return strippedStore
}

const getFiltersFromLocalStorage = () =>
  get<FilterContextType['filterStore']>(LS_FILTER_KEY)

export const updateFiltersInLocalStorage = (
  pathname: string,
  {
    pagination = undefined, // wipe by default when filters change
    filters = null,
    sort = null,
    extra = null,
    session = null,
  }: FilterObjectType
) => {
  const filtersClone = { ...getFiltersFromLocalStorage() }

  // Null = don't update
  // Undefined = reset/ update to empty
  // Anything else = update
  filtersClone[pathname] = {
    ...filtersClone[pathname],
    ...(!isNull(sort) ? { sort } : {}),
    ...(!isNull(filters) ? { filters } : {}),
    ...(!isNull(pagination) ? { pagination } : {}),
    ...(!isNull(extra) ? { extra } : {}),
    ...(!isNull(session) ? { session } : {}),
  }

  set(LS_FILTER_KEY, stripSession(filtersClone))

  return filtersClone
}

export const FilterProvider = ({ children }) => {
  const storedFilters = useMemo(getFiltersFromLocalStorage, [])

  const checkedQuery = useRef(undefined)
  const { pathname, query, replace } = useRouter()

  const [filterStore, setFilterStore] = useState(
    storedFilters || DEFAULT_FILTERS
  )

  const updateFilters = (filters: FilterObjectType) => {
    const filtersClone = updateFiltersInLocalStorage(pathname, filters)
    setFilterStore(filtersClone)
  }

  const clearFilters = () => {
    const filtersClone = { ...filterStore }

    // persist extra filters
    // but clear everything else
    filtersClone[pathname] = {
      ...(filtersClone[pathname]?.extra
        ? { extra: filtersClone[pathname]?.extra }
        : {}),
    }

    set(LS_FILTER_KEY, stripSession(filtersClone))

    // clear query params
    if (query?.filter) {
      replace({ pathname, query: {} }, undefined, { shallow: true })
    }

    setFilterStore(filtersClone)
  }

  const clearAllFilters = () => {
    remove(LS_FILTER_KEY)
    setFilterStore({})
  }

  useEffect(() => {
    // if the current checked query is for this route
    if (checkedQuery.current === pathname) {
      // check if the query still exists, if it is then assume the same query is being used
      if (query?.filter) {
        return
      } else {
        // clear the checked route as its not in use anymore
        checkedQuery.current = undefined
      }
    }

    if (query?.filter && query?.filter !== 'null') {
      checkedQuery.current = pathname
      const queryFilter = JSON.parse(query.filter?.toString())
      const currentFilters = filterStore[pathname] || {}
      const filtersClone = { ...filterStore }

      filtersClone[pathname] = {
        ...currentFilters,
        // purposely overwrite the filters with the query filter
        filters: { ...queryFilter },
      }

      setFilterStore(filtersClone)
    }
  }, [query, filterStore, pathname])

  const routeFilters = filterStore[pathname] || {}

  return (
    <FilterContext.Provider
      value={{
        updateFilters,
        clearAllFilters,
        clearFilters,
        filterStore,
        routeFilters,
      }}
    >
      {children}
    </FilterContext.Provider>
  )
}

export const useFilters = <T extends object = Record<string, unknown>>() =>
  useContext(FilterContext) as FilterContextType<T>
